import React from "react"
import { useNavigationStore } from "@/stores/navigation"
import { CalendarDays, Workflow, Link as LinkIcon, ShieldCheck, FileCheck, BarChart3, Microscope, Dna, FlaskConical, TestTube, CheckCircle2, TrendingUp, Waypoints, Rocket, Users, FolderCog, BookOpen } from "lucide-react"

const CellServeSetupPage: React.FC = () => {
  const navigationStore = useNavigationStore()

  const included = [
    { title: "Best-Practice Templates", body: "Prebuilt workflows, forms, checklists, and SOP samples for CAR-T, BMT, and advanced therapies.", Icon: Workflow },
    { title: "COI/COC Governance", body: "Chain of Identity & Chain of Custody checkpoints, attestations, and auditability in CellCX.", Icon: Dna },
    { title: "Capacity & Admissions", body: "Bed types, limits, SLA targets, and dashboards for confident admissions planning.", Icon: CalendarDays },
    { title: "Integrations Guidance", body: "Data mapping patterns for EHR/LIMS and secure exchange for orders, statuses, and results.", Icon: LinkIcon },
    { title: "Quality & Compliance", body: "Validation artifacts, role permissions, and audit-ready logs to support oversight.", Icon: ShieldCheck },
    { title: "Lab & Mfg Blueprints", body: "In-house or outsourced lab steps, QC gates, and hand-offs standardized across partners.", Icon: Microscope },
    { title: "Clinical Trial Enablement", body: "Visit windows, document packages, and protocol-specific workflows.", Icon: TestTube },
    { title: "Dashboards & KPIs", body: "Operational metrics, throughput, and SLA performance at a glance.", Icon: BarChart3 },
  ]

  const adoptionOptions = [
    {
      title: "Use It All",
      tagline: "Deploy the complete CellServe Setup for rapid onboarding.",
      bullets: [
        "Out-of-the-box workflows, forms, SOPs, and dashboards",
        "Fastest path to go-live",
        "Customize over time as needs evolve",
      ],
      Icon: Rocket,
    },
    {
      title: "Mix & Match",
      tagline: "Blend CellServe assets with your existing processes.",
      bullets: [
        "Keep what works, standardize the rest",
        "Map to your EHR/LIMS",
        "Adopt modules by team or site",
      ],
      Icon: Waypoints,
    },
    {
      title: "Start Small, Expand Later",
      tagline: "Begin with one area and scale as you grow.",
      bullets: [
        "Pilot a lab or trial flow",
        "Add modules and dashboards over time",
        "Evolve toward full best-practice coverage",
      ],
      Icon: TrendingUp,
    },
  ]

  const phases = [
    { step: "01", title: "Discovery & Fit", body: "Clarify objectives, scope, data flows, and governance. Align on success metrics.", Icon: Users },
    { step: "02", title: "Configuration", body: "Import or tailor templates; map data; set SLAs, roles, and permissions in CellCX.", Icon: FolderCog },
    { step: "03", title: "Validation", body: "Dry-runs and protocol checks; confirm COI/COC controls and alerts.", Icon: FileCheck },
    { step: "04", title: "Enablement", body: "Train staff, activate super-users, and finalize go-live readiness.", Icon: BookOpen },
    { step: "05", title: "Go-Live & Optimize", body: "Launch with dashboards; iterate on KPIs, throughput, and quality.", Icon: BarChart3 },
  ]

  const faqs = [
    { q: "Can I use CellCX without CellServe?", a: "Yes. CellCX can be fully configured with your own templates and workflows. CellServe adds optional best-practice content, workflows, guidelines and 'out of the box' expertise to accelerate time to value.", Icon: LinkIcon },
    { q: "How secure is the platform?", a: "CellCX is HIPAA-ready, SOC 2 Type II / SOC 3 certified, supports SSO/SAML, and aligns with GDPR-Aligned / DPF principles.", Icon: ShieldCheck },
    { q: "How long does implementation take?", a: "Typical implementations take from weeks to months depending upon the organization's requirements. Our team provides onboarding, training, and support.", Icon: FileCheck },
    { q: "How are updates managed?", a: "The CellServe team continuously reviews and refreshes best-practice content and workflows so your setup evolves alongside advances in the field.", Icon: FlaskConical },
  ]

  return (
    <div className="min-h-screen w-full text-white">
      <header className="w-full" style={{ background: "linear-gradient(90deg, #1D4E5F 0%, #2B7C85 100%)" }}>
        <div className="max-w-6xl mx-auto px-4 sm:px-6 py-16 sm:py-20 text-white">
          <div className="mb-8 sm:mb-10">
            <h2 className="text-6xl sm:text-7xl md:text-8xl leading-none tracking-tight">
              <span>CellServe</span>
              <span className="font-bold text-[#7AC943]"> Setup</span>
            </h2>
          </div>
          <div className="max-w-3xl">
            <h1 className="text-3xl sm:text-4xl md:text-5xl font-bold mb-3 sm:mb-4">Accelerate Your Cell Therapy Program with the CellServe Setup</h1>
            <p className="text-base sm:text-lg md:text-xl/8 opacity-95">A curated, continuously evolving configuration within CellCX that helps you launch, standardize, and scale cell therapy workflows faster — with confidence in proven best practices.</p>
            <div className="mt-6 sm:mt-8 flex flex-col sm:flex-row gap-3 w-full sm:w-auto">
              <button onClick={navigationStore.openDemoModal} className="w-full sm:w-auto text-center bg-white text-[#1D4E5F] font-semibold px-6 py-3 rounded-lg shadow-md hover:bg-gray-100 transition">Book a Demo</button>
              <a href="#intro" className="w-full sm:w-auto text-center bg-white/10 backdrop-blur-sm border border-white/20 text-white font-semibold px-6 py-3 rounded-lg hover:bg-white/20 transition">Learn More</a>
            </div>
          </div>
        </div>
      </header>

      <main className="bg-white text-slate-800">
        <section id="intro" className="max-w-6xl mx-auto px-4 sm:px-6 py-12 sm:py-14 scroll-mt-24 md:scroll-mt-28">
          <h3 className="text-2xl md:text-3xl font-bold text-slate-900 mb-3">Why the CellServe Setup?</h3>
          <p className="text-slate-600 mb-6">The CellServe Setup combines prebuilt workflows, forms, SOPs, and knowledge assets developed by CellServe and embedded within CellCX. It reflects real-world operational and clinical best practices — helping your organization start faster and scale smarter.</p>
          <div className="grid sm:grid-cols-2 gap-6">
            <div className="rounded-2xl border border-slate-200 p-6 shadow-sm bg-gradient-to-tr from-[#E0F2F1] to-[#F0F9F9]">
              <h4 className="font-semibold text-lg text-[#1D4E5F] mb-2">Always Evolving</h4>
              <p className="text-slate-700 leading-relaxed">The CellServe Setup isn’t static — it grows as cell therapy practices and standards evolve. The CellServe team regularly reviews and updates workflows, templates, and SOPs to reflect the latest operational and clinical best practices. Updates are delivered through CellCX so your program stays current without extra effort.</p>
            </div>
            <div className="rounded-2xl border border-slate-200 p-6 shadow-sm bg-gradient-to-tr from-[#E0F2F1] to-[#F0F9F9]">
              <h4 className="font-semibold text-lg text-[#1D4E5F] mb-2">Flexible Adoption</h4>
              <p className="text-slate-700 leading-relaxed">Use as much or as little of the CellServe Setup as needed. Many teams start with the out-of-the-box configuration for a rapid launch and gradually tailor or extend modules to match their workflows and SLAs.</p>
            </div>
          </div>
        </section>

        <section id="whats-included" className="max-w-6xl mx-auto px-4 sm:px-6 py-12 sm:py-14 bg-gradient-to-br from-[#E7F6F6] to-[#F5FBFB] rounded-3xl">
          <h3 className="text-2xl md:text-3xl font-bold text-slate-900 mb-2">What’s in the CellServe Setup</h3>
          <p className="text-slate-600 mb-6">A preconfigured library mapped into CellCX — ready to tailor for your environment.</p>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5 sm:gap-6">
            {included.map(({ title, body, Icon }, i) => (
              <div key={i} className="rounded-2xl border border-slate-200 p-5 sm:p-6 shadow-sm bg-white/70 backdrop-blur-[1px]">
                <div className="flex items-start gap-3 mb-2">
                  <span className="inline-flex items-center justify-center w-10 h-10 rounded-xl" style={{ background: "#E0F2F1" }}>
                    <Icon className="w-5 h-5" style={{ color: "#1D4E5F" }} aria-hidden="true" />
                  </span>
                  <h4 className="font-semibold text-slate-900 text-lg leading-snug">{title}</h4>
                </div>
                <p className="text-slate-600 text-base leading-relaxed">{body}</p>
              </div>
            ))}
          </div>
        </section>

        <section id="packages" className="max-w-6xl mx-auto px-4 sm:px-6 py-12 sm:py-14">
          <h3 className="text-2xl md:text-3xl font-bold text-slate-900 mb-2">Flexible Adoption</h3>
          <p className="text-slate-600 mb-6">There are no fixed packages. With the CellServe Setup you can use as much or as little as you want. Start quickly, then tailor and expand as you grow.</p>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5 sm:gap-6">
            {adoptionOptions.map(({ title, tagline, bullets, Icon }, i) => (
              <div key={i} className="rounded-2xl border border-slate-200 p-6 shadow-sm bg-gradient-to-tr from-[#E0F2F1] to-[#F0F9F9]">
                <div className="flex items-center gap-3 mb-3">
                  <Icon className="w-6 h-6" style={{ color: "#1D4E5F" }} />
                  <h4 className="font-semibold" style={{ color: "#1D4E5F" }}>{title}</h4>
                </div>
                <p className="text-slate-700 mb-4">{tagline}</p>
                <ul className="space-y-2 text-slate-700">
                  {bullets.map((b, j) => (
                    <li key={j} className="flex items-start gap-2"><CheckCircle2 className="w-4 h-4" style={{ color: "#1D4E5F" }} /> {b}</li>
                  ))}
                </ul>
              </div>
            ))}
          </div>
        </section>

        <section id="phases" className="max-w-6xl mx-auto px-4 sm:px-6 py-12 sm:py-14 bg-gradient-to-br from-[#EAF7F7] to-[#F7FCFC] rounded-3xl">
          <h3 className="text-2xl md:text-3xl font-bold text-slate-900 mb-2">Onboarding Phases</h3>
          <p className="text-slate-600 mb-6">Structured for clarity and speed — from discovery to optimization.</p>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-5 sm:gap-6">
            {phases.map(({ step, title, body, Icon }, i) => (
              <div key={i} className="rounded-2xl border border-slate-200 p-6 bg-white text-center shadow-sm">
                <div className="flex justify-center mb-3">
                  <span className="inline-flex items-center justify-center w-12 h-12 rounded-full" style={{ background: "#E0F2F1" }}>
                    <Icon className="w-6 h-6" style={{ color: "#1D4E5F" }} aria-hidden="true" />
                  </span>
                </div>
                <h4 className="font-semibold text-slate-900 text-lg mb-1">{step}. {title}</h4>
                <p className="text-slate-600 text-sm leading-relaxed">{body}</p>
              </div>
            ))}
          </div>
        </section>

        <section id="evolving" className="max-w-6xl mx-auto px-4 sm:px-6 py-12 sm:py-14">
          <h3 className="text-2xl md:text-3xl font-bold text-slate-900 mb-2">Always Evolving</h3>
          <div className="rounded-2xl border border-slate-200 p-6 shadow-sm bg-gradient-to-tr from-[#E0F2F1] to-[#F0F9F9]">
            <p className="text-slate-700 leading-relaxed">The CellServe Setup grows as clinical and operational practice evolves. The CellServe team periodically reviews and refreshes workflows, templates, and SOPs so you benefit from current best practices. Improvements are delivered through CellCX, along with compatibility updates as the platform advances.</p>
          </div>
        </section>

        <section className="max-w-6xl mx-auto px-4 sm:px-6 py-12 sm:py-14 border-t border-slate-200">
          <h3 className="text-2xl md:text-3xl font-bold text-slate-900 mb-6">FAQ</h3>
          <ul className="space-y-4">
            {faqs.map(({ q, a, Icon }, i) => (
              <li key={i} className="border border-slate-200 rounded-lg p-4 bg-gradient-to-tr from-[#E0F2F1] to-[#F0F9F9]">
                <details className="group">
                  <summary className="flex items-center gap-2 font-medium cursor-pointer" style={{ color: "#1D4E5F" }}>
                    <Icon className="w-5 h-5" style={{ color: "#1D4E5F" }} /> {q}
                  </summary>
                  <p className="mt-2 text-slate-700 leading-relaxed">{a}</p>
                </details>
              </li>
            ))}
          </ul>
        </section>

        <section id="contact" className="relative mt-6 sm:mt-8">
          <div className="absolute inset-0" style={{ background: "linear-gradient(90deg, #1D4E5F 0%, #2B7C85 100%)" }} />
          <div className="relative max-w-6xl mx-auto px-4 sm:px-6 py-12 sm:py-14 text-white">
            <div className="grid lg:grid-cols-2 gap-8 sm:gap-10 items-center">
              <div>
                <h3 className="text-2xl sm:text-3xl font-bold mb-2 sm:mb-3">Ready to accelerate your cell therapy program?</h3>
                <p className="opacity-90 text-base sm:text-lg">See how CellServe and CellCX help you start smarter and scale faster.</p>
              </div>
              <div className="flex flex-col sm:flex-row gap-3 lg:justify-end w-full sm:w-auto">
                <button onClick={navigationStore.openDemoModal} className="w-full sm:w-auto text-center bg-white text-[#1D4E5F] font-semibold px-6 py-3 rounded-lg shadow-md hover:bg-gray-100 transition">Book a Demo</button>
                <a href="#intro" className="w-full sm:w-auto text-center bg-white/10 backdrop-blur-sm border border-white/20 text-white font-semibold px-6 py-3 rounded-lg hover:bg-white/20 transition">Learn More</a>
              </div>
            </div>
          </div>
        </section>

        <section className="max-w-6xl mx-auto px-4 sm:px-6 py-8 sm:py-10 text-slate-500 text-sm">
          CellServe Setup is an option that clients can choose to deploy as part of CellCX which is part of the EceptionistCX Collaborative Healthcare Platform — easy to implement, easy to learn, and easy to evolve.
        </section>
      </main>
    </div>
  )
}

export default CellServeSetupPage


